import i18n from '../i18n';

/**
 * 統一數字格式化顯示函數
 * @param amount 數字金額（字串或數字）
 * @returns 格式化後的字串
 */
export const formatNumberForDisplay = (amount: string | number): string => {
  const num = typeof amount === 'number' ? amount : parseFloat(String(amount).replace(/,/g, ''));
  if (isNaN(num)) return 'N/A';
  
  if (num === 0) return '0';
  
  if (num < 0.0001) return '< 0.0001';
  
  if (num >= 1000000) {
    return (num / 1000000).toFixed(2) + 'M';
  }
  
  if (num >= 1000) {
    return (num / 1000).toFixed(2) + 'K';
  }
  
  if (num >= 1) {
    return num.toFixed(2);
  }
  
  return num.toFixed(4);
};

/**
 * 格式化錢包地址顯示（保留前6位和後4位）
 * @param address 完整的錢包地址
 * @returns 格式化後的地址字串
 */
export const formatWalletAddress = (address: string): string => {
  if (!address) return '';
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

/**
 * 格式化用戶名稱顯示（限制長度並截斷）
 * @param username 用戶名稱
 * @param maxLength 最大顯示長度，預設為 12 個字元
 * @returns 格式化後的用戶名稱字串
 */
export const formatUsername = (username: string | null | undefined, maxLength: number = 12): string => {
  if (!username || typeof username !== 'string') {
    return '';
  }
  
  // 移除首尾空白
  const trimmedUsername = username.trim();
  
  if (trimmedUsername.length <= maxLength) {
    return trimmedUsername;
  }
  
  // 截斷並添加省略號
  return trimmedUsername.slice(0, maxLength - 3) + '...';
};

/**
 * 格式化秒數為 HH:MM:SS 格式
 * @param seconds 總秒數
 * @returns 格式化的時間字串 (HH:MM:SS)
 */
export const formatSecondsToHMS = (seconds: number): string => {
  if (seconds <= 0) return '00:00:00';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  
  // 確保所有數字都是兩位數
  const paddedHours = String(hours).padStart(2, '0');
  const paddedMinutes = String(minutes).padStart(2, '0');
  const paddedSeconds = String(remainingSeconds).padStart(2, '0');
  
  return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
};

// 保留原有的 formatTokenAmount 名稱作為別名，確保向後兼容
export const formatTokenAmount = formatNumberForDisplay;

// 添加 formatNumber 作為別名，用於戰力排行榜等功能
export const formatNumber = formatNumberForDisplay;

const getDisplayLocale = () => {
  if (i18n?.resolvedLanguage) return i18n.resolvedLanguage;
  if (i18n?.language) return i18n.language;

  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }

  return 'en';
};

/**
 * 依照當前語言設定格式化日期時間
 * @param value 日期物件或可以被 Date 解析的值
 * @param options Intl 日期時間格式化選項
 * @returns 本地化後的日期時間字串
 */
export const formatDateTime = (value: Date | string | number, options?: Intl.DateTimeFormatOptions): string => {
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return '';

  const locale = getDisplayLocale();
  return date.toLocaleString(locale, {
    hour12: true,
    ...options
  });
};

/**
 * 以整數格式化數字（用於人數等不需要小數的場景）
 * @param amount 數字或字串
 * @returns 無小數點的格式化數字字串
 */
export const formatInteger = (amount: string | number): string => {
  const num = typeof amount === 'number' ? amount : parseFloat(String(amount).replace(/,/g, ''));
  if (isNaN(num)) return 'N/A';

  return Math.round(num).toLocaleString();
};

/**
 * 使用 Fisher-Yates 演算法隨機打亂陣列
 * @param array 要打亂的陣列
 * @returns 打亂後的新陣列（不會修改原陣列）
 */
export const shuffleArray = <T>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

/**
 * 格式化角色ID顯示（取UUID前8位 + 序號補零3位）
 * @param uuid 角色的完整UUID
 * @param drawSequence 角色的抽取序號
 * @returns 格式化後的角色ID字串，例如：0e118245-001
 */
export const formatCharacterId = (uuid: string, drawSequence: number): string => {
  if (!uuid) return '';

  const uuidPrefix = uuid.slice(0, 8);
  const sequenceSuffix = String(drawSequence).padStart(3, '0');

  return `${uuidPrefix}-${sequenceSuffix}`;
};
